import React, { useEffect, useState, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Message } from '@dev-streetbeat/chat';
import { useTasks } from '@dev-streetbeat/chat';
import { TextResponse } from '../responses/TextResponse';
import { MessageArtifact } from 'pages/assistant/components/artifacts/MessageArtifact';
import { FinecoAgentIcon } from 'shared/assets/svg/FinecoAgentIcon';
import styles from './chatMessage.module.scss';
import PortfolioSkeleton from '../PortfolioSkeleton';
import useThreadsStore from 'shared/store/threadsStore';
import { ChatFinecoCopyIcon } from 'shared/assets/svg/ChatFinecoCopyIcon';
import { ChatFinecoCopiedIcon } from 'shared/assets/svg/ChatFinecoCopiedIcon';
import Breakpoint from 'shared/components/responsive/Breakpoint';
import { useTabletAndBelowMediaQuery } from 'shared/store/responsiveStore';
import classNames from 'classnames';
import ResponseFeedback from './ResponseFeedback';
import { useParams } from 'react-router-dom';
import TaskStatuses from './TaskStatuses';

type Props = {
  message?: Message;
  thinking?: boolean;
  isConnectionOpen?: boolean;
  onSend?: (text: string) => void;
  disabled?: boolean;
  skeletonLoader?: boolean;
};

const CopyButton = memo(({ handleCopy, copied }: { handleCopy: () => void; copied: boolean }) => {
  const { t } = useTranslation();
  return (
    <div onClick={handleCopy} className={styles.chatCopyContainer}>
      <div className={styles.chatCopyInner}>
        <span>{t('global.copy')}</span>
        {copied ? (
          <ChatFinecoCopiedIcon className={styles.copyButtonIcons} />
        ) : (
          <ChatFinecoCopyIcon className={styles.copyButtonIcons} />
        )}
      </div>
    </div>
  );
});

CopyButton.displayName = 'CopyButton';

export const ChatAgentMessage: React.FC<Props> = memo(
  ({ message, thinking, isConnectionOpen, onSend, disabled, skeletonLoader }) => {
    const { t } = useTranslation();
    const { threadId } = useParams();
    const [copied, setCopied] = useState(false);
    const [finecoPortfolioAndPortfolioStoryTogether, setFinecoPortfolioAndPortfolioStoryTogether] = useState(false);

    const isTabletOrBelow = useTabletAndBelowMediaQuery();

    const hideTempStatusUpdate = useThreadsStore((state) => state.hideTempStatusUpdate);

    const handleCopy = useCallback(() => {
      const textToCopy = message?.content?.map((item) => item.text.value).join(' ');
      if (textToCopy) {
        navigator.clipboard.writeText(textToCopy).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        });
      }
    }, [message?.content]);

    const getResponseComponent = useCallback(() => {
      if (message?.content?.[0]?.type === 'text') {
        return <TextResponse message={message} />;
      }
    }, [message]);

    useEffect(() => {
      if (!message?.artifacts) return;

      const hasPortfolio = message.artifacts.some(
        (artifact: any) => artifact.type === 'custom_data' && artifact.custom_data?.type === 'fineco_portfolio',
      );

      const hasPortfolioStory = message.artifacts.some(
        (artifact: any) => artifact.type === 'custom_data' && artifact.custom_data?.type === 'portfolio_story',
      );

      setFinecoPortfolioAndPortfolioStoryTogether(hasPortfolio && hasPortfolioStory);
    }, [message?.artifacts]);

    // Determine if we should show feedback (only for completed non-temporary messages)
    const shouldShowFeedback =
      message?.id && message.id !== 'temp' && message.id !== 'temp-status-update' && !thinking && !skeletonLoader;

    if (!message) return null;

    if (thinking && !hideTempStatusUpdate) {
      return (
        <div className={styles.chatAgentContainer}>
          <div className={styles.chatAgentStatusUpdateContainer}>
            <FinecoAgentIcon className="icon-size-32-32" />
            <div className={styles.statusContainer}>
              <div className={styles.agentStatusMessages}>
                <TaskStatuses />
              </div>
              <PortfolioSkeleton />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.chatAgentContainer}>
        {skeletonLoader && !hideTempStatusUpdate && (
          <div className={styles.chatAgentStatusUpdateContainer}>
            <FinecoAgentIcon className="icon-size-32-32" />
            <div className={styles.statusContainer}>
              <div className={styles.agentStatusMessages}>
                <TaskStatuses />
              </div>
              <PortfolioSkeleton />
            </div>
          </div>
        )}
        {!skeletonLoader && (
          <div className="mt-2">
            <div
              className={classNames('flex min-w-0 flex-nowrap items-start', {
                'gap-4': !isTabletOrBelow,
                'flex-col': isTabletOrBelow,
              })}
            >
              <div
                className={classNames('flex-shrink-0', {
                  'flex gap-4': isTabletOrBelow,
                })}
                style={{
                  width: isTabletOrBelow ? 'fit-content' : 32,
                  height: 32,
                }}
              >
                <FinecoAgentIcon className="icon-size-32-32" />
                <Breakpoint md down>
                  <div className={styles.agentTitle}>{t('assistant.agent')}</div>
                </Breakpoint>
              </div>
              <div className="w-full min-w-0 flex-1">
                <Breakpoint lg up>
                  <div className={styles.agentTitle}>{t('assistant.agent')}</div>
                </Breakpoint>

                <div className="group relative break-words">{getResponseComponent()}</div>
                <div className={styles.artifactsContainer}>
                  {message.artifacts?.map((artifact, index) => (
                    <div key={index}>
                      <MessageArtifact
                        finecoPortfolioAndPortfolioStoryTogether={finecoPortfolioAndPortfolioStoryTogether}
                        message={message}
                        artifact={artifact}
                        isConnectionOpen={isConnectionOpen}
                        onSend={onSend}
                        disabled={disabled}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className={styles.messageControls}>
              {shouldShowFeedback && threadId && <ResponseFeedback messageId={message.id} />}
            </div>
          </div>
        )}
      </div>
    );
  },
);

ChatAgentMessage.displayName = 'ChatAgentMessage';

export default ChatAgentMessage;
